import React, { createContext, useContext, useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { ShoppingBag, Trash2, RotateCcw } from 'lucide-react';

const CartContext = createContext();

// Helper function to reliably compare items based on ID and selected color variant
const isSameVariant = (item, targetId, targetColor) => {
  if (item.id !== targetId) return false;

  const itemColor = typeof item.selectedColor === 'object' ? item.selectedColor?.name : item.selectedColor;
  const targetColorName = typeof targetColor === 'object' ? targetColor?.name : targetColor;

  return itemColor === targetColorName;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(() => {
    try {
      const savedCart = localStorage.getItem('vedhyas_cart');
      return savedCart ? JSON.parse(savedCart) : [];
    } catch (error) {
      console.error('Error reading vedhyas_cart from localStorage:', error);
      return [];
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('vedhyas_cart', JSON.stringify(cart));
    } catch (error) {
      console.error('Error saving vedhyas_cart to localStorage:', error);
    }
  }, [cart]);

  // Add item or increment quantity if variant already exists
  const addToCart = (product, quantity = 1) => {
    setCart((prevCart) => {
      const existingIndex = prevCart.findIndex((item) =>
        isSameVariant(item, product.id, product.selectedColor)
      );

      if (existingIndex > -1) {
        const updatedCart = [...prevCart];
        updatedCart[existingIndex].quantity += quantity;
        return updatedCart;
      }

      return [...prevCart, { ...product, quantity }];
    });

    // Custom Vedhyas Royal Toast Notification on Add
    toast.custom(
      (t) => (
        <div
          className={`${
            t.visible ? 'animate-enter' : 'animate-leave'
          } max-w-sm w-full bg-[#20050A] text-[#FDFBF7] shadow-2xl rounded-2xl pointer-events-auto flex items-center justify-between p-4 border border-[#4A0E17]`}
        >
          <div className="flex items-center space-x-3">
            <div className="p-2.5 bg-[#4A0E17] text-[#E8DFC8] rounded-xl border border-[#E8DFC8]/20">
              <ShoppingBag className="w-4 h-4" />
            </div>
            <div className="text-left">
              <p className="text-xs font-serif font-bold text-[#FDFBF7] line-clamp-1">
                {product.name}
              </p>
              <p className="text-[10px] text-[#E8DFC8]/80 font-sans mt-0.5">
                {quantity > 1 ? `Added (${quantity}) items to bag` : 'Added to your Vedhyas bag'}
              </p>
            </div>
          </div>
          <button
            onClick={() => toast.dismiss(t.id)}
            className="text-[10px] font-serif font-bold text-[#E8DFC8] hover:text-[#FDFBF7] px-2 py-1 uppercase tracking-widest transition-colors"
          >
            Close
          </button>
        </div>
      ),
      { duration: 2500 }
    );
  };

  // Update item quantity directly or remove if newQuantity <= 0
  const updateQuantity = (productId, newQuantity, selectedColor = null) => {
    if (newQuantity <= 0) {
      removeFromCart(productId, selectedColor);
      return;
    }

    setCart((prevCart) =>
      prevCart.map((item) =>
        isSameVariant(item, productId, selectedColor)
          ? { ...item, quantity: newQuantity }
          : item
      )
    );
  };

  // Remove specific item variant from cart
  const removeFromCart = (productId, selectedColor = null) => {
    let removedItemName = 'Item';

    setCart((prevCart) => {
      const target = prevCart.find((item) => isSameVariant(item, productId, selectedColor));
      if (target) removedItemName = target.name;

      return prevCart.filter((item) => !isSameVariant(item, productId, selectedColor));
    });

    // Custom Toast Notification on Remove
    toast.custom(
      (t) => (
        <div
          className={`${
            t.visible ? 'animate-enter' : 'animate-leave'
          } max-w-sm w-full bg-[#20050A] text-[#FDFBF7] shadow-2xl rounded-2xl pointer-events-auto flex items-center justify-between p-4 border border-[#4A0E17]`}
        >
          <div className="flex items-center space-x-3">
            <div className="p-2.5 bg-red-950/80 text-red-200 rounded-xl border border-red-800/40">
              <Trash2 className="w-4 h-4" />
            </div>
            <div className="text-left">
              <p className="text-xs font-serif font-bold text-[#FDFBF7] line-clamp-1">
                {removedItemName}
              </p>
              <p className="text-[10px] text-red-300 font-sans mt-0.5">Removed from shopping bag</p>
            </div>
          </div>
          <button
            onClick={() => toast.dismiss(t.id)}
            className="text-[10px] font-serif font-bold text-[#E8DFC8]/80 hover:text-[#FDFBF7] px-2 py-1 uppercase tracking-widest transition-colors"
          >
            Close
          </button>
        </div>
      ),
      { duration: 2500 }
    );
  };

  // Clear entire cart
  const clearCart = () => {
    setCart([]);
    toast.custom(
      (t) => (
        <div
          className={`${
            t.visible ? 'animate-enter' : 'animate-leave'
          } max-w-sm w-full bg-[#20050A] text-[#FDFBF7] shadow-2xl rounded-2xl pointer-events-auto flex items-center justify-between p-4 border border-[#4A0E17]`}
        >
          <div className="flex items-center space-x-3">
            <div className="p-2.5 bg-[#4A0E17] text-[#E8DFC8] rounded-xl border border-[#E8DFC8]/20">
              <RotateCcw className="w-4 h-4" />
            </div>
            <p className="text-xs font-serif font-bold text-[#FDFBF7]">Shopping bag cleared</p>
          </div>
          <button
            onClick={() => toast.dismiss(t.id)}
            className="text-[10px] font-serif font-bold text-[#E8DFC8] hover:text-[#FDFBF7] px-2 py-1 uppercase tracking-widest transition-colors"
          >
            Close
          </button>
        </div>
      ),
      { duration: 2000 }
    );
  };

  // Get total price
  const getCartTotal = () => {
    return cart.reduce((total, item) => total + item.price * item.quantity, 0);
  };

  // Get total count of items
  const getCartCount = () => {
    return cart.reduce((count, item) => count + item.quantity, 0);
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        updateQuantity,
        removeFromCart,
        clearCart,
        getCartTotal,
        getCartCount,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};